import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Loader2,
  TrendingUp,
  Award,
  Users,
  Star
} from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

interface Level {
  id: string;
  name: string;
  min_points: number;
  max_points: number | null;
  color: string;
  icon: string;
  description: string;
  order_index: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

const mockLevels: Level[] = [
  {
    id: 'level-1',
    name: 'Sprout',
    min_points: 0,
    max_points: 99,
    color: 'text-green-600',
    icon: 'Sprout',
    description: 'Just starting your eco journey',
    order_index: 1,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    updated_at: '2024-12-08T00:00:00Z'
  },
  {
    id: 'level-2',
    name: 'Sapling',
    min_points: 100,
    max_points: 499,
    color: 'text-emerald-600',
    icon: 'TreePine',
    description: 'Growing your environmental impact',
    order_index: 2,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    updated_at: '2024-12-08T00:00:00Z'
  },
  {
    id: 'level-3',
    name: 'Tree',
    min_points: 500,
    max_points: 1499,
    color: 'text-teal-600',
    icon: 'TreePine',
    description: 'Established eco-warrior',
    order_index: 3,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    updated_at: '2024-12-08T00:00:00Z'
  },
  {
    id: 'level-4',
    name: 'Forest Guardian',
    min_points: 1500,
    max_points: 4999,
    color: 'text-blue-600',
    icon: 'Shield',
    description: 'Protecting our planet',
    order_index: 4,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    updated_at: '2024-12-08T00:00:00Z'
  },
  {
    id: 'level-5',
    name: 'Eco Champion',
    min_points: 5000,
    max_points: 14999,
    color: 'text-purple-600',
    icon: 'Award',
    description: 'Leading by example',
    order_index: 5,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    updated_at: '2024-12-08T00:00:00Z'
  },
  {
    id: 'level-6',
    name: 'Planet Protector',
    min_points: 15000,
    max_points: null,
    color: 'text-yellow-600',
    icon: 'Star',
    description: 'Ultimate environmental hero',
    order_index: 6,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    updated_at: '2024-12-08T00:00:00Z'
  }
];

export default function AdminLevels() {
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyLevel: Omit<Level, 'id' | 'created_at' | 'updated_at'> = {
    name: '',
    min_points: 0,
    max_points: null,
    color: 'text-gray-600',
    icon: 'Star',
    description: '',
    order_index: levels.length + 1,
    active: true
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        setLevels(mockLevels);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_levels')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) {
        console.error('Error fetching levels:', error);
        setLevels(mockLevels);
        return;
      }

      setLevels(data || []);
    } catch (error) {
      console.error('Error fetching levels:', error);
      setLevels(mockLevels);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (level: Level | Omit<Level, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setSaving(true);

      if (!isSupabaseConfigured) {
        if ('id' in level) {
          // Update existing
          setLevels(prev => prev.map(l => l.id === level.id ? level : l));
        } else {
          // Create new
          const newLevel = {
            ...level,
            id: `level-${Date.now()}`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setLevels(prev => [...prev, newLevel]);
        }
        setEditingLevel(null);
        setIsCreating(false);
        setSaving(false);
        return;
      }

      if ('id' in level) {
        // Update existing level
        const { error } = await supabase
          .from('user_levels')
          .update(level)
          .eq('id', level.id);

        if (error) {
          console.error('Error updating level:', error);
          return;
        }
      } else {
        // Create new level
        const { error } = await supabase
          .from('user_levels')
          .insert(level);

        if (error) {
          console.error('Error creating level:', error);
          return;
        }
      }

      await fetchLevels();
      setEditingLevel(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving level:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (levelId: string) => {
    if (!confirm('Are you sure you want to delete this level?')) return;

    try {
      if (!isSupabaseConfigured) {
        setLevels(prev => prev.filter(l => l.id !== levelId));
        return;
      }

      const { error } = await supabase
        .from('user_levels')
        .delete()
        .eq('id', levelId);

      if (error) {
        console.error('Error deleting level:', error);
        return;
      }

      await fetchLevels();
    } catch (error) {
      console.error('Error deleting level:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Level Management</h2>
          <p className="text-gray-600 mt-2">Configure user progression levels and point requirements</p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add Level</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{levels.length}</p>
              <p className="text-sm text-gray-600">Total Levels</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Award className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {levels.filter(l => l.active).length}
              </p>
              <p className="text-sm text-gray-600">Active Levels</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Star className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {levels.length > 0 ? Math.max(...levels.map(l => l.max_points || 0)) : 0}
              </p>
              <p className="text-sm text-gray-600">Max Points</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Users className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {levels.reduce((sum, l) => sum + (l.max_points || 0) - l.min_points, 0)}
              </p>
              <p className="text-sm text-gray-600">Total Range</p>
            </div>
          </div>
        </div>
      </div>

      {/* Level Form Modal */}
      {(editingLevel || isCreating) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingLevel ? 'Edit Level' : 'Create New Level'}
              </h3>
              <button
                onClick={() => {
                  setEditingLevel(null);
                  setIsCreating(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <LevelForm
              level={editingLevel || emptyLevel}
              onSave={handleSave}
              saving={saving}
            />
          </div>
        </div>
      )}

      {/* Levels List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Point Range
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {levels.map((level) => (
                <tr key={level.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gray-100 p-2 rounded-lg">
                        <Star className={`h-5 w-5 ${level.color}`} />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{level.name}</div>
                        <div className="text-sm text-gray-500">Order: {level.order_index}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {level.min_points.toLocaleString()} - {level.max_points ? level.max_points.toLocaleString() : '∞'} pts
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {level.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      level.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {level.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setEditingLevel(level)}
                        className="p-2 text-gray-600 hover:text-emerald-600 rounded-lg transition-colors"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(level.id)}
                        className="p-2 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Level Form Component
function LevelForm({ 
  level, 
  onSave, 
  saving 
}: { 
  level: Level | Omit<Level, 'id' | 'created_at' | 'updated_at'>;
  onSave: (level: Level | Omit<Level, 'id' | 'created_at' | 'updated_at'>) => void;
  saving: boolean;
}) {
  const [formData, setFormData] = useState(level);

  const iconOptions = ['Star', 'Award', 'Trophy', 'Shield', 'TreePine', 'Leaf', 'Crown', 'Gem'];
  const colorOptions = [
    { value: 'text-green-600', label: 'Green' },
    { value: 'text-emerald-600', label: 'Emerald' },
    { value: 'text-teal-600', label: 'Teal' },
    { value: 'text-blue-600', label: 'Blue' },
    { value: 'text-purple-600', label: 'Purple' },
    { value: 'text-yellow-600', label: 'Yellow' },
    { value: 'text-orange-600', label: 'Orange' },
    { value: 'text-red-600', label: 'Red' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Level Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g., Eco Champion"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Order Index</label>
          <input
            type="number"
            value={formData.order_index}
            onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 1 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            min="1"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          rows={3}
          placeholder="Describe this level..."
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Points</label>
          <input
            type="number"
            value={formData.min_points}
            onChange={(e) => setFormData({ ...formData, min_points: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            min="0"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Points</label>
          <input
            type="number"
            value={formData.max_points || ''}
            onChange={(e) => setFormData({ ...formData, max_points: e.target.value ? parseInt(e.target.value) : null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            min="0"
            placeholder="Leave empty for unlimited"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
          <select
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
          <select
            value={formData.color}
            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {colorOptions.map(color => (
              <option key={color.value} value={color.value}>{color.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-gray-700">Active</span>
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="submit"
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Level</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}